import type { OrgStructureResponse } from "@/api/configuration-server";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { OrgStructureTree } from "@/components/agents/configuration-server/OrgStructureTree";
import {
  getOrgMetadata,
  getOrgUnitContext,
  type OrgUnitContext,
} from "@/api/org-execution";
import { useOrgUnitsScroll } from "@/hooks/useManagementLists";
import { useDebouncedSearch } from "@/hooks/useDebouncedSearch";
import { stageKeys } from "@/hooks/useConfigServerStages";
import { LIST_QUERY_OPTIONS } from "@/lib/config-pagination";
import type { OrgTreeNode, ScopeVerdict } from "@/lib/org-structure-tree";
import { LoadMore } from "./Paging";
import { StageLoading } from "./StageLoading";
export function BoundedOrgTool({
  projectId,
  artifactId,
  tool,
  onClose,
  onVerdict,
  onChanged,
}: {
  projectId: string;
  artifactId: string;
  tool: { kind: "details" | "edit"; key: string } | { kind: "create" };
  onClose: () => void;
  onVerdict: (
    node: OrgTreeNode,
    verdict: Exclude<ScopeVerdict, "pending">,
  ) => void;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const key = "key" in tool ? tool.key : undefined;
  const context = useQuery<OrgUnitContext | { snapshot: OrgStructureResponse }>(
    {
      queryKey: [
        ...stageKeys.all(projectId),
        "management",
        "org-unit-context",
        artifactId,
        key,
      ],
      queryFn: async () =>
        key
          ? await getOrgUnitContext(projectId, key, artifactId)
          : await getOrgMetadata(projectId, artifactId),
      ...LIST_QUERY_OPTIONS,
    },
  );
  const [parentLevel, setParentLevel] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [q, commitSearch] = useDebouncedSearch(search);
  const filters = { unitTypes: parentLevel ? [parentLevel] : [], q };
  const pending = useOrgUnitsScroll(
    projectId,
    artifactId,
    { verdict: "pending", ...filters },
    !!parentLevel,
  );
  const approved = useOrgUnitsScroll(
    projectId,
    artifactId,
    { verdict: "approved", ...filters },
    !!parentLevel,
  );
  const excluded = useOrgUnitsScroll(
    projectId,
    artifactId,
    { verdict: "out_of_scope", ...filters },
    !!parentLevel,
  );
  const snapshot = context.data?.snapshot;
  const candidates = useMemo(() => {
    const byLevel: Record<
      string,
      { id: string; name: string | null; targetId?: string | null }[]
    > = {};
    for (const level of snapshot?.levels ?? [])
      byLevel[level.level_code] = level.nodes.map((node) => ({
        id: node.id,
        name: node.name,
        targetId: node.target_id,
      }));
    if (parentLevel) {
      const rows = [pending.data, approved.data, excluded.data].flatMap(
        (data) => data?.pages.flatMap((page) => page.items) ?? [],
      );
      byLevel[parentLevel] = [
        ...new Map(
          [
            ...(byLevel[parentLevel] ?? []),
            ...rows.map((row) => ({
              id: row.id,
              name: row.name ?? null,
              targetId: row.target_id,
            })),
          ].map((item) => [item.id, item]),
        ).values(),
      ];
    }
    return byLevel;
  }, [snapshot, parentLevel, pending.data, approved.data, excluded.data]);
  if (context.isPending) return <StageLoading variant="panel" />;
  if (context.isError)
    return (
      <div role="alert">
        <button onClick={() => void context.refetch()}>{t("Retry")}</button>
      </div>
    );
  return (
    <OrgStructureTree
      projectId={projectId}
      artifactId={artifactId}
      view="list"
      hideExecution
      boundedData={snapshot ?? null}
      boundedMigrationItems={
        context.data && "row" in context.data
          ? [
              {
                item_type: context.data.row.level_code,
                item_key: context.data.row.item_key,
                status: (context.data.row.migration_status ??
                  "pending") as OrgTreeNode["migrationStatus"],
                outcome: null,
                external_id: null,
                attempts: 0,
                last_run_id: null,
              },
            ]
          : []
      }
      tool={{ request: tool, onClose }}
      onRequestManagementVerdict={onVerdict}
      onManagementChanged={onChanged}
      knownChildCounts={
        context.data && "row" in context.data
          ? { [context.data.row.key]: context.data.row.child_count }
          : undefined
      }
      externalParentCandidates={candidates}
      onParentLevelChange={setParentLevel}
      parentControls={
        <div className="space-y-2">
          <input
            type="search"
            aria-label={t("Search code or name…")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitSearch();
            }}
            className="h-9 w-full rounded border px-3"
          />
          <LoadMore
            hasMore={[pending, approved, excluded].some(
              (query) => query.hasNextPage,
            )}
            loading={[pending, approved, excluded].some(
              (query) => query.isFetching,
            )}
            onLoadMore={() => {
              for (const query of [pending, approved, excluded])
                if (query.hasNextPage) void query.fetchNextPage();
            }}
          />
        </div>
      }
    />
  );
}
